import type { Expr } from '../core/types';
import { SAFE, SN } from './glsl';

export interface SignaturePoint {
  nm: number; val: number; role: string; bandIndex: number;
}

/** A named material, defined by the points you clicked on it. */
export interface TrainingClass {
  id: string;
  name: string;
  colour: [number, number, number];
  /** One entry per training click. */
  points: SignaturePoint[][];
}

export const DEG = 57.2957795;

/**
 * The class spectrum: the mean of its training points.
 *
 * Averaging is safe here in a way it would not be for a brightness-based measure,
 * because the spectral angle ignores magnitude — so a class can pool sunlit and shaded
 * examples of the same material and still come out with a usable signature.
 *
 * Only bands present in every training point contribute, so a partially-masked click
 * cannot skew one band of the mean.
 */
export function classMean(points: SignaturePoint[][]): SignaturePoint[] | null {
  if (!points.length) return null;
  const acc = new Map<number, { nm: number; role: string; sum: number; n: number }>();
  for (const p of points) {
    for (const pt of p) {
      const e = acc.get(pt.bandIndex) ?? { nm: pt.nm, role: pt.role, sum: 0, n: 0 };
      e.sum += pt.val;
      e.n++;
      acc.set(pt.bandIndex, e);
    }
  }
  const out = [...acc.entries()]
    .filter(([, e]) => e.n === points.length)
    .map(([bandIndex, e]) => ({ bandIndex, nm: e.nm, role: e.role, val: e.sum / e.n }))
    .sort((a, b) => a.nm - b.nm);
  return out.length >= 2 ? out : null;
}

/** Spectral angle between two spectra, in degrees. Exact — this runs on the CPU. */
export function angleBetween(a: SignaturePoint[], b: SignaturePoint[]): number | null {
  const bm = new Map(b.map((p) => [p.bandIndex, p.val]));
  let dot = 0, na = 0, nb = 0;
  for (const p of a) {
    const q = bm.get(p.bandIndex);
    if (q === undefined) continue;
    dot += p.val * q;
    na += p.val * p.val;
    nb += q * q;
  }
  if (!(na > 0 && nb > 0)) return null;
  return Math.acos(Math.min(1, Math.max(-1, dot / Math.sqrt(na * nb)))) * 180 / Math.PI;
}

/**
 * How tightly a class's own training points agree, in degrees. A class whose examples
 * disagree with each other is badly drawn, and no threshold will rescue it.
 */
export function classSpread(cls: TrainingClass): number | null {
  const mean = classMean(cls.points);
  if (!mean || cls.points.length < 2) return null;
  const angles = cls.points
    .map((p) => angleBetween(p, mean))
    .filter((a): a is number => a != null);
  return angles.length ? Math.max(...angles) : null;
}

/**
 * Angle between every pair of class means. Two classes a couple of degrees apart
 * cannot be separated by any classifier, and the map will look like noise — knowing
 * that before interpreting it is worth more than the map itself.
 */
export function separability(classes: TrainingClass[]): { a: number; b: number; deg: number }[] {
  const means = classes.map((c) => classMean(c.points));
  const out: { a: number; b: number; deg: number }[] = [];
  for (let i = 0; i < means.length; i++) {
    for (let j = i + 1; j < means.length; j++) {
      const mi = means[i], mj = means[j];
      if (!mi || !mj) continue;
      const deg = angleBetween(mi, mj);
      if (deg != null) out.push({ a: i, b: j, deg });
    }
  }
  return out.sort((x, y) => x.deg - y.deg);
}

export interface ClassifyResult {
  /** Colour channel k of whichever class wins at this pixel. */
  chan: (k: number) => Expr;
  /** Zero where the closest class is still further than the threshold. */
  alpha: Expr;
  /** Index into `used` of the winning class, for export and readouts. */
  winner: Expr;
  /** Classes that contributed, in the order the expressions use. */
  used: TrainingClass[];
}

/**
 * Nearest-angle classification.
 *
 * cos a = (t·u) / |t| for a unit-length class vector u. The pixel magnitude |t| is
 * common to every class, so it cancels out of the comparison — ranking classes needs
 * only the dot products, and |t| is computed once for the threshold. That keeps this
 * cheap enough to run per fragment with several classes.
 *
 * @param maxAngleDeg pixels further than this from every class stay unclassified,
 * which is what separates an honest map from one that labels bare soil as canopy.
 */
export function samClassifyExpr(
  classes: TrainingClass[],
  bandExpr: (bandIndex: number) => Expr | null,
  maxAngleDeg: number,
): ClassifyResult | null {
  const means = classes
    .map((c) => ({ cls: c, mean: classMean(c.points) }))
    .filter((x): x is { cls: TrainingClass; mean: SignaturePoint[] } => x.mean != null);
  if (!means.length) return null;

  // Only bands every class shares can take part in the comparison.
  const common = means
    .map((m) => new Set(m.mean.map((p) => p.bandIndex)))
    .reduce((a, b) => new Set([...a].filter((x) => b.has(x))));
  const bands = [...common].sort((a, b) => a - b);
  if (bands.length < 2) return null;

  const exprs = bands.map(bandExpr);
  if (exprs.some((e) => e == null)) return null;

  // Unit-length class vectors, so a dot product is already the cosine numerator.
  const units = means.map((m) => {
    const byBand = new Map(m.mean.map((p) => [p.bandIndex, p.val]));
    const v = bands.map((b) => byBand.get(b) ?? 0);
    const norm = Math.sqrt(v.reduce((a, x) => a + x * x, 0));
    return norm > 1e-12 ? v.map((x) => x / norm) : null;
  });
  if (units.some((u) => u == null)) return null;

  const dots: Expr[] = units.map((u) => {
    let acc: Expr | null = null;
    u!.forEach((w, i) => {
      const term: Expr = ['*', exprs[i]!, SN(w, 0)];
      acc = acc ? ['+', acc, term] : term;
    });
    return acc!;
  });

  let sumSq: Expr | null = null;
  for (const e of exprs) {
    const sq: Expr = ['*', e!, e!];
    sumSq = sumSq ? ['+', sumSq, sq] : sq;
  }
  const mag: Expr = ['^', sumSq!, 0.5];

  /** Pick the value belonging to whichever class has the largest dot product. */
  const pickBy = (value: (i: number) => Expr): Expr => {
    if (dots.length === 1) return value(0);
    const args: Expr[] = [];
    for (let i = 0; i < dots.length - 1; i++) {
      const wins = dots
        .map((d, j) => (j === i ? null : (['>=', dots[i]!, d] as Expr)))
        .filter((x): x is Expr => x != null);
      args.push(wins.length === 1 ? wins[0]! : (['all', ...wins] as Expr), value(i));
    }
    args.push(value(dots.length - 1));
    return ['case', ...args];
  };

  const cosThreshold = Math.cos(Math.min(89.9, Math.max(0.1, maxAngleDeg)) * Math.PI / 180);
  const bestCos: Expr = ['/', pickBy((i) => dots[i]!), SAFE(mag)];

  return {
    chan: (k) => pickBy((i) => means[i]!.cls.colour[k]!),
    alpha: ['case', ['>=', bestCos, SN(cosThreshold, 0)], 1, 0],
    winner: pickBy((i) => i),
    used: means.map((m) => m.cls),
  };
}

/** Default palette for new classes — distinguishable, and not the index ramps. */
export const CLASS_COLOURS: [number, number, number][] = [
  [230, 97, 75], [121, 196, 162], [111, 168, 220], [242, 169, 59],
  [197, 139, 224], [232, 212, 77], [120, 200, 120], [220, 120, 170],
];
